// src/pages/LMS/Tests/TestResults.js
import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Button, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Chip, IconButton, Dialog,
  DialogTitle, DialogContent, DialogActions, Grid, Card, CardContent,
  FormControl, InputLabel, Select, MenuItem, CircularProgress
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Visibility as ViewIcon,
  Download as DownloadIcon,
  CheckCircle as CorrectIcon,
  Cancel as WrongIcon
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import axiosInstance from '../../../../utils/axiosInstance';

const TestResults = () => {
  const { chatbotId, courseId, moduleId } = useParams();
  const navigate = useNavigate();

  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('');
  const [detailDialog, setDetailDialog] = useState({ open: false, result: null });

  useEffect(() => {
    loadResults();
  }, [moduleId, filterStatus]);

  const loadResults = async () => {
    setLoading(true);
    try {
      const params = { module_id: moduleId };
      if (filterStatus) params.status = filterStatus;

      const response = await axiosInstance.get(`/api/lms/test-results`, { params });
      setResults(response.data.data);
    } catch (error) {
      console.error('Error loading results:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = async (resultId) => {
    try {
      const response = await axiosInstance.get(`/api/lms/test-results/${resultId}`);
      setDetailDialog({ open: true, result: response.data.data });
    } catch (error) {
      console.error('Error loading result details:', error);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 }, mt: { xs: 2, sm: 10 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton onClick={() => navigate(-1)}>
            <BackIcon />
          </IconButton>
          <Typography variant="h4" fontWeight={700} sx={{ ml: 2, fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
            Test Results
          </Typography>
        </Box>
        <FormControl size="small" sx={{ minWidth: { xs: 120, sm: 150 } }}>
          <InputLabel>Filter by Status</InputLabel>
          <Select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            label="Filter by Status"
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="passed">Passed</MenuItem>
            <MenuItem value="failed">Failed</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
        <Table sx={{ minWidth: 700 }}>
          <TableHead>
            <TableRow>
              <TableCell>Student</TableCell>
              <TableCell>Attempt</TableCell>
              <TableCell>Score</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Time Taken</TableCell>
              <TableCell>Submitted</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {results.map((result) => (
              <TableRow key={result.id} hover>
                <TableCell>
                  {result.student?.first_name} {result.student?.last_name}
                </TableCell>
                <TableCell>
                  {result.attempt_number}/{result.max_attempts}
                </TableCell>
                <TableCell>
                  <Typography fontWeight={600}>{result.score}%</Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={result.passed ? 'Passed' : 'Failed'}
                    color={result.passed ? 'success' : 'error'}
                    size="small"
                  />
                </TableCell>
                <TableCell>{result.time_taken_minutes} min</TableCell>
                <TableCell>
                  {new Date(result.submitted_at).toLocaleString()}
                </TableCell>
                <TableCell align="right">
                  <IconButton
                    size="small"
                    onClick={() => handleViewDetails(result.id)}
                  >
                    <ViewIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Detail Dialog */}
      <Dialog
        open={detailDialog.open}
        onClose={() => setDetailDialog({ open: false, result: null })}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Test Result Details</DialogTitle>
        <DialogContent>
          {detailDialog.result && (
            <>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6} sm={3}>
                  <Card>
                    <CardContent>
                      <Typography variant="h5" color="primary">
                        {detailDialog.result.score}%
                      </Typography>
                      <Typography variant="caption">Score</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Card>
                    <CardContent>
                      <Typography variant="h5" color="success.main">
                        {detailDialog.result.correct_answers}
                      </Typography>
                      <Typography variant="caption">Correct</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Card>
                    <CardContent>
                      <Typography variant="h5" color="error.main">
                        {detailDialog.result.incorrect_answers}
                      </Typography>
                      <Typography variant="caption">Incorrect</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Card>
                    <CardContent>
                      <Typography variant="h5">
                        {detailDialog.result.time_taken_minutes}m
                      </Typography>
                      <Typography variant="caption">Time</Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              <Typography variant="h6" gutterBottom>
                Question Breakdown
              </Typography>
              {detailDialog.result.answers?.map((answer, index) => (
                <Paper key={index} sx={{ p: 2, mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                    {answer.is_correct ? (
                      <CorrectIcon color="success" sx={{ mr: 1 }} />
                    ) : (
                      <WrongIcon color="error" sx={{ mr: 1 }} />
                    )}
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Question {index + 1}
                      </Typography>
                      <Typography variant="body2" paragraph>
                        {answer.question_text}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Your answer: {answer.student_answer}
                      </Typography>
                      {!answer.is_correct && (
                        <Typography variant="caption" display="block" color="success.main">
                          Correct answer: {answer.correct_answer}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Paper>
              ))}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialog({ open: false, result: null })}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TestResults;